import React, { useState, useEffect } from 'react';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  Typography, 
  Box, 
  Grid, 
  Divider, 
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close, Receipt } from '@mui/icons-material';
import { Apartment, Trip, Traveller } from '@/types';
import { getSkiPassPrice, getTransportationPrice, getMembershipFee, getApartmentPrice } from '@/services/billCalculationService';
import { travellerService } from '@/services/travellerService';
import { formatCurrency } from '@/lib/utils';
import { useI18n } from '@/hooks/useI18n';
import MemberStatusChip from '@/components/ui/MemberStatusChip';

interface ApartmentBillProps {
  apartment: Apartment;
  trip: Trip;
}

const ApartmentBill: React.FC<ApartmentBillProps> = ({ 
  apartment, 
  trip
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [apartmentTravellers, setApartmentTravellers] = useState<Traveller[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n();

  const { skiPasses, apartmentTypes, transportationOptions } = trip;

  useEffect(() => {
    if (!isOpen || !trip.id || !apartment.id) return;

    setIsLoading(true);
    const unsubscribe = travellerService.listenToTravellersByApartment(
      trip.id,
      apartment.id,
      (travellers) => {
        setApartmentTravellers(travellers);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isOpen, trip.id, apartment.id]);

  const getSkiPassName = (skiPassType?: string) => {
    const skiPass = skiPasses.find(pass => pass.id === skiPassType);
    return skiPass ? skiPass.name : t('apartment.bill.noSkiPass');
  };

  const getTransportationName = (transportationType?: string) => {
    const transportation = transportationOptions.find(t => t.id === transportationType);
    return transportation ? transportation.name : t('apartment.bill.noTransportation');
  };

  const getApartmentType = () => {
    return apartmentTypes.find(s => s.id === apartment.apartmentTypeId);
  };

  const accommodationCostPerPerson = getApartmentPrice(apartment, apartmentTypes);

  const totalSkiPassCost = apartmentTravellers.reduce((sum, traveller) => 
    sum + getSkiPassPrice(traveller, skiPasses, trip), 0);
  const totalTransportationCost = apartmentTravellers.reduce((sum, traveller) => 
    sum + getTransportationPrice(traveller, transportationOptions), 0);
  const totalMembershipCost = apartmentTravellers.reduce((sum, traveller) => 
    sum + getMembershipFee(traveller, trip), 0);
  
  const apartmentType = getApartmentType();
  const accommodationCost = accommodationCostPerPerson * apartmentTravellers.length;
  const grandTotal = accommodationCost + totalSkiPassCost + totalTransportationCost + totalMembershipCost;

  if (apartment.currentOccupancy === 0) {
    return null;
  }

  return (
    <>
      <Button 
        variant="outlined" 
        size="small" 
        onClick={() => setIsOpen(true)}
        sx={{ width: '100%' }}
        startIcon={<Receipt />}
      >
        {t('apartment.bill.generateBill')}
      </Button>

      <Dialog 
        open={isOpen} 
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {t('apartment.bill.billFor', { apartmentName: apartment.name })}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setIsOpen(false)}
            sx={{
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 3 }}>
          {isLoading ? (
            <Box 
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 256,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ mt: 2, space: 3 }}>
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                      {t('apartment.bill.apartmentDetails')}
                    </Typography>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {t('apartment.bill.apartment')}:
                      </Typography>
                      <Typography variant="body2">{apartment.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {t('apartment.bill.class')}:
                      </Typography>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {apartment.class}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {t('apartment.bill.apartmentType')}:
                      </Typography>
                      <Typography variant="body2">
                        {apartmentType?.name || t('apartment.bill.notAssigned')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {t('apartment.bill.pricePerPerson')}:
                      </Typography>
                      <Typography variant="body2">
                        € {accommodationCostPerPerson}/{t('apartment.bill.week')}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mb: 3 }}>
                <CardHeader 
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                      {t('apartment.bill.travellerBreakdown')}
                    </Typography>
                  }
                />
                <CardContent>
                  {apartmentTravellers.length > 0 ? (
                    <Box sx={{ space: 2 }}>
                      {apartmentTravellers.map((traveller) => {
                        const skiPassPrice = getSkiPassPrice(traveller, skiPasses, trip);
                        const transportationPrice = getTransportationPrice(traveller, transportationOptions);
                        const membershipFee = trip ? getMembershipFee(traveller, trip) : 0;
                        const accommodationCost = accommodationCostPerPerson;
                        const totalCostPerTraveller = accommodationCost + skiPassPrice + transportationPrice + membershipFee;

                        return (
                          <Card 
                            key={traveller.id} 
                            variant="outlined" 
                            sx={{ mb: 2, p: 2 }}
                          >
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                  {traveller.name}
                                </Typography>
                                <MemberStatusChip 
                                  isMember={traveller.isSkiClubMember}
                                  size="small"
                                />
                              </Box>
                            </Box>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('apartment.bill.accommodation')}:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  € {accommodationCost}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('apartment.bill.skiPass')} ({getSkiPassName(traveller.skiPassType)}):
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  € {skiPassPrice}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('apartment.bill.transport')} ({getTransportationName(traveller.transportationType)}):
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  € {transportationPrice}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('apartment.bill.membershipFee')}:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  € {membershipFee}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                fontWeight: 'medium'
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {t('apartment.bill.subtotalFor', { travellerName: traveller.name })}:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                € {totalCostPerTraveller}
                              </Typography>
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('apartment.bill.noTravellersYet')}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {apartmentTravellers.length > 0 && (
                <Card>
                  <CardHeader 
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        {t('apartment.bill.totalSummary')}
                      </Typography>
                    }
                  />
                  <CardContent>
                    <Box sx={{ space: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{t('apartment.bill.totalAccommodationCost')}:</Typography>
                        <Typography variant="body2">€ {formatCurrency(accommodationCost)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{t('apartment.bill.totalSkiPassCost')}:</Typography>
                        <Typography variant="body2">€ {formatCurrency(totalSkiPassCost)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{t('apartment.bill.totalTransportationCost')}:</Typography>
                        <Typography variant="body2">€ {formatCurrency(totalTransportationCost)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">{t('apartment.bill.totalMembershipFees')}:</Typography>
                        <Typography variant="body2">€ {formatCurrency(totalMembershipCost)}</Typography>
                      </Box>
                      <Divider />
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          mt: 2,
                          fontWeight: 'bold'
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {t('apartment.bill.grandTotal')}:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          € {formatCurrency(grandTotal)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApartmentBill;
